import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import CustomSelect from '../../../CustomReactSelectComponent'
import InputGroup from '../../../InputGroup'
import PhoneNumber from '../../../CustomNumberFormat'
import Switcher from '../../../CustomSwitchComponent'
import { Row, Col, Button, Form } from 'reactstrap'
import { SHIPPERS_FILTERS } from '../../../../constants/contactFilterCategory'
import { useDispatch } from 'react-redux'

// Filter configuration to reduce repetitive code
const FILTER_CONFIG = {
  // Multi-select filters
  source_id: { filterName: 'source_id', optionsKey: 'SourceType' },
  equipment_type: { filterName: 'equipment_type', optionsKey: 'LoadEquipment' },
  address_state: { filterName: 'address_state', optionsKey: 'States' },
  destination_state: { filterName: 'destination_state', optionsKey: 'States' },
  origin_state: { filterName: 'origin_state', optionsKey: 'States' },
  created_by: { filterName: 'created_by', optionsKey: 'Users' },
  grade: { filterName: 'divisions.grade', optionsKey: 'ProspectGrade', localKey: 'grade' },
  load_type: { filterName: 'load_type', optionsKey: 'LoadType' },
  nationality_id: { filterName: 'nationality_id', optionsKey: 'Nationality' },
  salesperson: { filterName: 'divisions.assigned', optionsKey: 'Salespersons', localKey: 'salesperson' },
  red_flag_flagged_by: { filterName: 'red_flag.flagged_by', optionsKey: 'Users', localKey: 'red_flag_flagged_by' },
  red_flag_division_id: {
    filterName: 'red_flag.division_id',
    optionsKey: 'Divisions',
    localKey: 'red_flag_division_id',
  },
  tms_type_id: { filterName: 'tms_type_id', optionsKey: 'TMSType' },
  effort_level: {
    filterName: 'divisions.effort_level',
    optionsKey: 'EffortLevel',
  },

  // Single-select filters
  annual_sales_id: { filterName: 'cg.annual_sales_id', optionsKey: 'AnnualSales', isSingle: true },

  // Special filters with custom logic
  load_cost: { filterName: 'load_cost', optionsKey: 'LoadCost', isSpecial: true },
  red_flag_status: {
    filterName: 'red_flag.status',
    optionsKey: 'RedFlagstatus',
    isSpecial: true,
    localKey: 'red_flag_status',
  },

  // Boolean filters
  has_active_tasks: { filterName: 'has_active_tasks', isBoolean: true },
  no_active_tasks: { filterName: 'no_active_tasks', isBoolean: true },
}

const FilterInputsCustomerLogistics = ({
  selectedFilters,
  handleFiltersChanges,
  loadData,
  setFiltersValue,
  removeFilter,
  selectedFiltersValues,
  getOptions,
  options,
  allContactsOnly,
}) => {
  const [data, setData] = useState({})
  const dispatch = useDispatch()

  // Unified filter dispatching logic
  const handleFilterDispatching = useCallback(
    (filterName, filterValues, localFilterName = filterName) => {
      const value = data[localFilterName]

      // Handle arrays (multi-select fields)
      if (Array.isArray(value)) {
        if (value.length > 0) {
          dispatch(setFiltersValue(filterName, filterValues))
        } else {
          dispatch(removeFilter(filterName))
        }
      }
      // Handle single values (single-select fields like annual_sales_id)
      else if (value !== null && value !== undefined) {
        dispatch(setFiltersValue(filterName, filterValues))
      } else {
        dispatch(removeFilter(filterName))
      }
    },
    [data, dispatch, setFiltersValue, removeFilter]
  )

  // Special mapping functions
  const mapRedFlagStatusIds = useCallback(() => {
    const { red_flag_status } = data
    const mappedValues = []
    if (red_flag_status?.length > 0) {
      red_flag_status.forEach((id) => mappedValues.push(id === 94 ? 2 : 1)) //94 Closed
    }
    return mappedValues
  }, [data])

  const mapLoadCostValues = useCallback(() => {
    return data?.load_cost?.map((el) => {
      const element = options?.LoadCost?.find((option) => option.name === el)
      return element?.value
    })
  }, [data, options])

  // Unified useEffect for filter dispatching
  useEffect(() => {
    Object.entries(FILTER_CONFIG).forEach(([key, config]) => {
      const { filterName, localKey, isSpecial } = config
      const localFilterKey = localKey || key

      if (!data.hasOwnProperty(localFilterKey)) return

      if (isSpecial) {
        if (key === 'load_cost') {
          const values = mapLoadCostValues()
          handleFilterDispatching(filterName, values, localFilterKey)
        } else if (key === 'red_flag_status') {
          const mappedValues = mapRedFlagStatusIds()
          handleFilterDispatching(filterName, mappedValues, localFilterKey)
        }
      } else if (config.isBoolean) {
        // Handle boolean filters
        if (data[localFilterKey]) {
          dispatch(setFiltersValue(filterName, data[localFilterKey]))
        } else {
          dispatch(removeFilter(filterName))
        }
      } else {
        handleFilterDispatching(filterName, data[localFilterKey], localFilterKey)
      }
    })
  }, [data, handleFilterDispatching, mapLoadCostValues, mapRedFlagStatusIds])

  useEffect(() => {
    if (!selectedFilters.salesperson || selectedFilters.salesperson.length === 0) {
      dispatch(removeFilter('has_active_tasks'))
      dispatch(removeFilter('divisions.assigned'))
    }
  }, [selectedFilters.salesperson])

  // Load options on mount
  useEffect(() => {
    getOptions(SHIPPERS_FILTERS)
  }, [getOptions])

  // Unified select change handler
  const handleSelectChange = useCallback((values, e, isMulti, isLoadCost) => {
    const { name } = e
    if (isMulti) {
      if (values) {
        const newValues = values.map((value) => (isLoadCost ? value.value : value.id || value.value))
        setData((prev) => ({ ...prev, [name]: newValues }))
      } else {
        setData((prev) => ({ ...prev, [name]: [] }))
      }
    } else {
      setData((prev) => ({ ...prev, [name]: values ? values.value || values.id : null }))
    }
  }, [])

  // Render field components
  const renderInputField = (key, label, type = 'text', extraProps = {}) => {
    const shouldRender = selectedFilters[key] || (extraProps.group && selectedFilters[extraProps.group])
    if (!shouldRender) return null

    return (
      <Col xs={3} key={key}>
        <InputGroup
          label={label}
          type={type}
          name={key}
          onChange={handleFiltersChanges}
          value={selectedFiltersValues[key]}
          {...extraProps}
        />
      </Col>
    )
  }

  const renderSelectField = (key, label, optionsKey, isMulti = true, extraProps = {}) => {
    const shouldRender = selectedFilters[key] || (extraProps.group && selectedFilters[extraProps.group])
    if (!shouldRender) return null

    return (
      <Col xs={3} key={key}>
        <CustomSelect
          label={label}
          name={key}
          defaultValue={data?.[key] || (isMulti ? [] : null)}
          onChange={(values, event) => handleSelectChange(values, event, isMulti)}
          isMulti={isMulti}
          isClearable
          options={options[optionsKey] || []}
          {...extraProps}
        />
      </Col>
    )
  }

  const renderPhoneField = (key, label) =>
    selectedFilters[key] && (
      <Col xs={3} key={key}>
        <PhoneNumber
          label={label}
          className='form-control-sm form-control'
          name={key}
          value={selectedFiltersValues[key]}
          placeholder={label}
          type='tel'
          format='(999) 999-9999'
          onChange={handleFiltersChanges}
          size={10}
          hidePhoneIcon
        />
      </Col>
    )

  const renderDateRangeField = (key, labelFrom, labelTo, fieldName = key, extraProps = {}) => {
    const shouldRender = selectedFilters[key] || (extraProps.group && selectedFilters[extraProps.group])
    if (!shouldRender) return null

    return (
      <React.Fragment key={key}>
        <Col xs={2}>
          <InputGroup
            label={labelFrom}
            name={fieldName}
            type='date'
            onChange={(event) => handleFiltersChanges(event, 'from')}
            value={selectedFiltersValues[fieldName]?.from}
          />
        </Col>
        <Col xs={2}>
          <InputGroup
            label={labelTo}
            name={fieldName}
            type='date'
            onChange={(event) => handleFiltersChanges(event, 'to')}
            value={selectedFiltersValues[fieldName]?.to}
          />
        </Col>
      </React.Fragment>
    )
  }

  const renderSwitchField = (key, label, parentKey) =>
    selectedFilters[key] ||
    (parentKey && selectedFilters[parentKey] && (
      <Col xs={3} key={key}>
        <Switcher labelName={label} name={key} checked={selectedFiltersValues[key]} onChange={handleFiltersChanges} />
      </Col>
    ))

  return (
    <>
      <Form noValidate onSubmit={(e) => e.preventDefault()}>
        <Row className='mt-3 filterInputs'>
          {/* Basic Input Fields */}
          {renderInputField('name', 'Name')}
          {renderInputField('address_city', 'City')}
          {renderInputField('email', 'Email')}
          {renderInputField('address_zip', 'Zip Code')}
          {renderInputField('contact_person', 'Contact Person', 'text', { name: 'owner_name' })}
          {/* Phone Field */}
          {renderPhoneField('business_phone', 'Business Phone')}
          {/* Select Fields */}
          {renderSelectField('source_id', 'Source', 'SourceType')}
          {renderSelectField('nationality_id', 'Preferred Language', 'Nationality')}
          {renderSelectField('address_state', 'State', 'States')}
          {renderSelectField('equipment_type', 'Equipment Type', 'LoadEquipment')}
          {renderSelectField('salesperson', 'Salesperson', 'Salespersons')}
          {renderSelectField('tms_type_id', 'TMSType', 'TMSType')}
          {/* Show has_active_tasks checkbox when salesperson is selected */}
          {selectedFilters.salesperson && data.salesperson && data.salesperson.length > 0 && (
            <>
              {renderSwitchField('has_active_tasks', 'Has Active Tasks', 'salesperson')}
              {renderSwitchField('no_active_tasks', 'No Tasks', 'salesperson')}
            </>
          )}
          {renderSelectField('grade', 'Grade', 'ProspectGrade')}
          {renderSelectField('created_by', 'Created By', 'Users')}
          {renderSelectField('load_type', 'Load Type', 'LoadType')}
          {/* Special Select Fields */}
          {renderSelectField('annual_sales_id', 'Annual Sales', 'AnnualSales', false)}
          {renderSelectField('load_cost', 'Load Cost', 'LoadCost', true, {
            defaultValue: selectedFiltersValues?.load_cost || [],
            onChange: (values, event) => handleSelectChange(values, event, true, true),
          })}
          {/* Destination Fields */}
          {selectedFilters.destination && (
            <React.Fragment key='destination'>
              {renderInputField('destination_city', 'Destination City', '', { group: 'destination' })}
              {renderSelectField('destination_state', 'Destination State', 'States', false, { group: 'destination' })}
              {renderInputField('destination_zip', 'Destination Zip', '', { group: 'destination' })}
            </React.Fragment>
          )}
          {/* Origin Fields */}
          {selectedFilters.origin && (
            <React.Fragment key='origin'>
              {renderInputField('origin_city', 'Origin City', '', { group: 'origin' })}
              {renderSelectField('origin_state', 'Origin State', 'States', true, { group: 'origin' })}
              {renderInputField('origin_zip', 'Origin Zip', '', { group: 'origin' })}
            </React.Fragment>
          )}
          {/* Date Range Fields */}
          {renderDateRangeField('date_established', 'Date Established from', 'Date Established to')}
          {renderDateRangeField('creation_date', 'Date Entered from', 'Date Entered to')}
          {renderDateRangeField(
            'last_contact_date',
            'Last Contact Date from',
            'Last Contact Date to',
            'divisions.last_contact_date'
          )}
          {/* Red Flag Fields */}
          {selectedFilters.red_flag && (
            <React.Fragment key='red_flag'>
              {renderSelectField('red_flag_status', 'Red Flag', 'RedFlagstatus', true, { group: 'red_flag' })}
              {renderSelectField('red_flag_flagged_by', 'Red Flag User', 'Users', true, { group: 'red_flag' })}
              {renderSelectField('red_flag_division_id', 'Red Flag Division', 'Divisions', true, { group: 'red_flag' })}
              {renderDateRangeField(
                'red_flag_creation_date',
                'Red Flag Date from',
                'Red Flag Date to',
                'red_flag.creation_date',
                { group: 'red_flag' }
              )}
            </React.Fragment>
          )}
          {/* Switch Fields */}
          {renderSwitchField('out_of_business', 'Out Of Business')}
          {/* Account Date (conditional) */}
          {selectedFilters?.account_date && allContactsOnly && (
            <React.Fragment key='account_date'>
              {renderDateRangeField('account_date', 'Account Date From', 'Account Date To', 'divisions.account_date')}
            </React.Fragment>
          )}
          {selectedFilters?.effort_level && renderSelectField('effort_level', 'Effort Level', 'EffortLevel')}
          {selectedFilters?.prospect_date && allContactsOnly && (
            <React.Fragment key='prospect_date'>
              {renderDateRangeField('prospect_date', 'Prospect Date From', 'Prospect Date To', 'csc.prospect_date')}
            </React.Fragment>
          )}
          {selectedFilters?.rfp_date && allContactsOnly && (
            <React.Fragment key='rfp_date'>
              {renderDateRangeField('rfp_date', 'RFP Date From', 'RFP Date To', 'cg.rfp_date')}
            </React.Fragment>
          )}
          {selectedFilters?.pending_account_date && allContactsOnly && (
            <React.Fragment key='pending_account_date'>
              {renderDateRangeField(
                'pending_account_date',
                'Pending Account Date From',
                'Pending Account Date To',
                'csc.pending_account_date'
              )}
            </React.Fragment>
          )}
          {selectedFilters?.dead_date && allContactsOnly && (
            <React.Fragment key='dead_date'>
              {renderDateRangeField('dead_date', 'Dead Date From', 'Dead Date To', 'cd.creation_date')}
            </React.Fragment>
          )}
          {selectedFilters?.nq_date && allContactsOnly && (
            <React.Fragment key='nq_date'>
              {renderDateRangeField('nq_date', 'NQ Date From', 'NQ Date To', 'cnq.creation_date')}
            </React.Fragment>
          )}
          {selectedFilters?.i_give_up && allContactsOnly && (
            <React.Fragment key='i_give_up'>
              {renderDateRangeField('i_give_up', 'I Give Up Date From', 'I Give Up Date To', 'cgu.creation_date')}
            </React.Fragment>
          )}
        </Row>
      </Form>

      <Row className='flex-row-reverse mr-3'>
        <Button
          className='btn btn-gold'
          type='submit'
          onClick={() => {
            // console.log(selectedFiltersValues)

            loadData()
          }}
        >
          Search
        </Button>
      </Row>
    </>
  )
}

FilterInputsCustomerLogistics.propTypes = {
  selectedFilters: PropTypes.object.isRequired,
  selectedFiltersValues: PropTypes.object,
  handleFiltersChanges: PropTypes.func.isRequired,
  loadData: PropTypes.func.isRequired,
  setFiltersValue: PropTypes.func.isRequired,
  removeFilter: PropTypes.func.isRequired,
  options: PropTypes.object.isRequired,
  getOptions: PropTypes.func.isRequired,
  allContactsOnly: PropTypes.bool,
}

FilterInputsCustomerLogistics.defaultProps = {
  selectedFiltersValues: {},
  allContactsOnly: false,
}

export default FilterInputsCustomerLogistics
